/**
 * `/crm/persons` — the people deals are made with. Searchable by name, paged,
 * sortable by name or by when they were added.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { CRM_PERMISSIONS, type PersonDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useDebounced } from '../../hooks/useDebounced';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { usePersonActions } from './actions';
import { createPerson, listPersons } from './api';
import { BLANK_PERSON, createPersonRequest, PersonFields, type PersonForm } from './forms';

export default function PersonsPage() {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const [term, setTerm] = useState('');
  const q = useDebounced(term.trim());

  const list = useListQuery({ defaultSort: 'name', resetKey: q });
  const persons = useAsync(() => listPersons({ ...list.query, ...(q ? { q } : {}) }), [list.key, q]);
  const actions = usePersonActions({ onChanged: () => persons.reload() });

  const crud = useCrudDialogs<PersonDto, PersonForm>({ blank: BLANK_PERSON, formFor: () => BLANK_PERSON });
  const create = useSubmit(async () => {
    const result = await createPerson(createPersonRequest(crud.form));
    persons.reload();
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('person.title')}
        description={t('person.description')}
        actions={
          can(CRM_PERMISSIONS.WRITE) && (
            <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => crud.openCreate()}>
              {t('person.new')}
            </Button>
          )
        }
      />

      <SearchField value={term} onChange={setTerm} placeholder={t('person.searchPlaceholder')} sx={{ width: '100%', maxWidth: 420, mb: 2 }} />

      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={persons}>
        {({ persons: rows, page }) => (
          <EntityTable<PersonDto>
            rows={rows}
            rowKey={(person) => person.id}
            list={list}
            page={page}
            empty={{
              icon: <PersonOutlinedIcon fontSize="inherit" />,
              title: q ? t('person.noMatches') : t('person.emptyTitle'),
              description: t('person.emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('field.name'),
                sortable: true,
                render: (person) => (
                  <Link component={RouterLink} to={`/crm/persons/${person.id}`} underline="hover" sx={{ fontWeight: 500 }}>
                    {person.name}
                  </Link>
                ),
              },
              {
                field: 'organization',
                label: t('field.organization'),
                render: (person) =>
                  person.organization && (
                    <Link component={RouterLink} to={`/crm/organizations/${person.organization.id}`} underline="hover">
                      {person.organization.name}
                    </Link>
                  ),
              },
              { field: 'email', label: t('field.email'), render: (person) => person.email ?? '' },
              { field: 'phone', label: t('field.phone'), width: 160, render: (person) => person.phone ?? '' },
              { field: 'owner', label: t('field.owner'), width: 170, render: (person) => person.owner.name },
            ]}
            actions={actions.rowActions}
            actionsWidth={100}
          />
        )}
      </AsyncContent>
      {actions.dialogs}

      <FormDialog
        open={crud.isOpen}
        title={t('person.createDialog.title')}
        submitLabel={t('person.createDialog.submit')}
        pending={create.pending}
        error={create.error}
        disabled={crud.form.name.trim() === ''}
        onClose={crud.close}
        onSubmit={async () => {
          if (await create.submit()) crud.close();
        }}
      >
        <PersonFields form={crud.form} patch={crud.patch} />
      </FormDialog>
    </>
  );
}
